/**
 * Catalogue of the player.command ops (published as `commands` in the API description): for every op a client may
 * send, the JSON Schema of its `args`, of the op's `result` (player.command `result`; the reply also carries the whole
 * new `gameState`), and the `details.reason` values of its VALIDATION_FAILED refusals (besides 'bad_args' for wrong
 * arguments, 'bad_state' for an unreadable stored state and 'no_profile' before the first profile.load, which every
 * op may give). A refused command changes nothing; a retry under the same opId gets the same reply.
 *
 * The rule ops live in src/game/rules/ops (their parseArgs is the authority for the arguments), the service ops in
 * src/server/core/services (battles.ts, auction.ts, commands.ts). tests/backend/protocol-contract.test.ts checks that
 * the catalogue lists exactly the ops the server accepts and validates it against the rule fixtures and real replies.
 */
import { COMMAND_LOG_SCHEMA } from './validators';
import { AUCTION_HOLDINGS, AUCTION_STATE, arrayOf, bool, described, int, mapOf, nullable, num, obj, oneOfValues, openObject, str } from './contract';
import type { JsonSchema } from './schema';

type S = JsonSchema;

export interface CommandInfo {
  description: string;
  /** JSON Schema of `args` (unknown fields are refused with 'bad_args'). */
  args: S;
  /** JSON Schema of the op's `result`. */
  result: S;
  /**
   * The refusal reasons of this op (VALIDATION_FAILED from the rules; INVALID_STATE / UNAVAILABLE where the service says
   * so); see the module comment for the ones every op may give.
   */
  reasons: string[];
  /** Implemented by a server service (its inputs come from the server's own state) rather than a rule op alone. */
  service?: true;
}

/** Arguments of an op that takes none. */
const NO_ARGS: S = { type: 'object', properties: {}, additionalProperties: false, description: 'no arguments ({})' };
const ID: S = { type: 'string', maxLength: 64, pattern: '^[A-Za-z0-9_]+$', description: 'an item id (DEF key)' };
const CURRENCY: S = oneOfValues('gold', 'diam');

/** Strict arguments object: exactly these fields (optional ones may be missing). */
function args(props: Record<string, S>, optional: readonly string[] = []): S {
  return { ...obj(props, optional), additionalProperties: false };
}

const LEVEL_UP: S = obj({ levels: int, skill_pts: int, perk_pts: int, diam: int, surpriseWeapons: arrayOf(str) }, [], 'the level-up the operation caused (levels 0 = none)');
const CLAIM_RESULT: S = obj({ gold: int, diam: int, exp: int, levelUp: LEVEL_UP });
const APOLOGY: S = nullable(obj({ luck: bool, merch: bool }));
const CRAFT_ITEM: S = args(
  { id: ID, tp: oneOfValues('weapon', 'object'), range: { type: 'integer', minimum: 1, maximum: 10 }, stolen: bool, crafted: bool },
  ['range', 'stolen', 'crafted'],
);
const CRAFT_RESULT: S = obj(
  {
    mode: oneOfValues('assemble', 'disassemble'),
    id: described(str, "the recipe's weapon ('' = no recipe matched)"),
    range: int,
    success: bool,
    error: described(str, "'req<N>' when the Crafting perk is too low"),
    new_recipe: bool,
    junk: described(arrayOf(int), '[count, junk number 1, ...]: obj_junk<N> added'),
    new_object: described(str, "object found by a disassembly ('' = none)"),
    errorScreen: oneOfValues('craft_too_diff', 'craft_limit'),
  },
  ['errorScreen'],
);
const SHOP_BUY_RESULT = (categories: string[]): S =>
  obj({ category: oneOfValues(...categories), id: str, currency: CURRENCY, price: int, count: int, newWeapon: bool });
const COPY_ARGS: S = args({ id: ID, stat: described(oneOfValues('', 's', 'c'), "'' = a bought copy, 's' = stolen, 'c' = crafted"), range: { type: 'integer', minimum: 1, maximum: 10 } });
const BATTLE_GRAVE: S = obj({ name: str, cpu_level: int });
const KILLS: S = args({ p: int, e: int, a: int, c: int, h: int, f: int, l: int });

const TIRED: S = obj({ before: num, after: num });

/** Every client-callable player.command op. */
export const COMMAND_CATALOGUE: Readonly<Record<string, CommandInfo>> = {
  // --- profile and progress
  'tutorial.close': { description: 'Closes the tutorial for good.', args: NO_ARGS, result: obj({}), reasons: ['not_available'] },
  'time.refresh': {
    description: 'Applies the time-based changes (tiredness recovery, Papa John hours) at the current server time.',
    args: NO_ARGS,
    result: obj({ papaHere: bool, tired: num }),
    reasons: [],
  },
  'settings.set': {
    description: 'Game settings: difficulty 0..4, hardcore, battle animations, the device time zone (minutes east of UTC; accepted at most once per 24 h).',
    args: args(
      {
        difficulty: { type: 'integer', minimum: 0, maximum: 4 },
        hardcore: bool,
        animation: bool,
        tzOffsetMin: { type: 'integer', minimum: -720, maximum: 840 },
      },
      ['difficulty', 'hardcore', 'animation', 'tzOffsetMin'],
    ),
    result: obj({ skill: int, no_animation: int, tzOffsetMin: int, changed: bool }, ['tzOffsetMin']),
    reasons: [],
    service: true,
  },
  'names.set': {
    description: 'Names of the worms (at most 5, 15 characters each, the characters the name field accepts).',
    args: args({ names: { type: 'array', maxItems: 5, items: { type: 'string', maxLength: 15 } } }),
    result: obj({ names: arrayOf(str) }),
    reasons: ['not_editable'],
  },
  'progress.startOver': { description: 'Starts the career over (the original "start over" of the progress screen).', args: NO_ARGS, result: obj({}), reasons: ['level_too_low'] },
  'skills.commit': {
    description: 'Commits the skill editor: target values of attack / protect / health / worms, target perk levels and perk slot purchases.',
    args: args(
      {
        attack: { type: 'integer', minimum: 0, maximum: 1_000_000 },
        protect: { type: 'integer', minimum: 0, maximum: 1_000_000 },
        health: { type: 'integer', minimum: 0, maximum: 1_000_000 },
        worms: { type: 'integer', minimum: 0, maximum: 1_000_000 },
        perks: { type: 'object', propertyNames: { pattern: '^[A-Za-z_][A-Za-z0-9_]{0,47}$' }, additionalProperties: { type: 'integer', minimum: 1, maximum: 100 }, description: 'perk id -> target level' },
        slots: { type: 'array', maxItems: 7, items: CURRENCY, description: 'perk slot purchases in order, each paid in gold or diamonds' },
      },
      ['attack', 'protect', 'health', 'worms', 'perks', 'slots'],
    ),
    result: obj({ skill_pts: int, perk_pts: int, gold: int, diam: int, newPerks: int }),
    reasons: ['cannot_decrease', 'not_enough_skill_pts', 'not_enough_perk_pts', 'not_enough_gold', 'not_enough_diam', 'limit_reached', 'no_free_slot', 'not_available', 'requirement_not_met', 'no_changes', 'unknown_item'],
  },
  'perks.reset': { description: 'Resets the perks for diamonds.', args: NO_ARGS, result: obj({ perk_pts: int, skill_pts: int }), reasons: ['not_enough_diam', 'level_too_low', 'not_available'] },
  'spec.choose': {
    description: 'Chooses the specialization 1..4.',
    args: args({ spec: { type: 'integer', minimum: 1, maximum: 4 } }),
    result: obj({ spec: int, perk_pts: int }),
    reasons: ['not_available', 'level_too_low'],
  },
  'achiev.claim': { description: 'Collects the achievement bonus.', args: NO_ARGS, result: CLAIM_RESULT, reasons: ['not_available'] },
  'task.claim': { description: 'Collects the bonus of the finished task.', args: NO_ARGS, result: CLAIM_RESULT, reasons: ['not_available'] },
  'present.claim': {
    description: 'Collects the daily present that profile.load reported (volatile.presentAvailable).',
    args: NO_ARGS,
    result: obj({ tween: int, gold: int, diam: int, skill_pts: int }),
    reasons: ['not_available'],
    service: true,
  },
  'tribute.claim': {
    description: 'Collects the hourly tribute of the zones the player and its clan hold.',
    args: NO_ARGS,
    result: obj({ terr_total: int, my_gold: int, clans_gold: int, gold: int, nextPaymentAt: described(int, 'epoch ms of the next possible claim') }),
    reasons: ['not_available'],
    service: true,
  },

  // --- shops and arsenal
  'shop.buy': {
    description: 'Buys an item of the shop for gold or diamonds.',
    args: args({ category: oneOfValues('weapons', 'help', 'flags', 'rip', 'impl', 'objs'), id: ID, currency: CURRENCY }),
    result: SHOP_BUY_RESULT(['weapons', 'help', 'flags', 'rip', 'impl', 'objs']),
    reasons: ['shop_closed', 'unknown_item', 'not_available', 'level_too_low', 'requirement_not_met', 'limit_reached', 'not_enough_gold', 'not_enough_diam'],
  },
  'papa.enter': {
    description: "Enters Papa John's shop while it is open (volatile.papaHere); fixes today's assortment.",
    args: NO_ARGS,
    result: obj({ lastPapa: int, weapons: arrayOf(str), objs: arrayOf(str) }),
    reasons: ['papa_not_here', 'papa_shop_visited'],
  },
  'papa.buy': {
    description: "Buys from Papa John's assortment of today.",
    args: args({ category: oneOfValues('weapons', 'objs'), id: ID, currency: CURRENCY }),
    result: SHOP_BUY_RESULT(['weapons', 'objs']),
    reasons: ['papa_not_here', 'papa_shop_closed', 'unknown_item', 'not_available', 'level_too_low', 'requirement_not_met', 'limit_reached', 'not_enough_gold', 'not_enough_diam'],
  },
  'papa.ask': {
    description: 'Asks Papa John when he comes again (a chance).',
    args: NO_ARGS,
    result: obj({ win: bool, prob: num, tomorrowHour: nullable(int) }),
    reasons: ['papa_not_here', 'already_asked', 'not_available'],
  },
  'arsenal.sell': {
    description: 'Sells an item that is not a weapon (the nine books go as a collection).',
    args: args({ category: oneOfValues('help', 'flags', 'rip', 'impl', 'objs'), id: ID }),
    result: obj({ category: str, id: str, gold: int, count: int, bookCollection: bool }),
    reasons: ['unknown_item', 'not_owned', 'not_sellable', 'collect_all_books'],
  },
  'arsenal.sellWeapon': {
    description: 'Sells one weapon copy (identified by its kind and rank).',
    args: COPY_ARGS,
    result: obj({ id: str, gold: int, count: int }),
    reasons: ['unknown_item', 'not_owned', 'not_sellable', 'limit_reached'],
  },
  'arsenal.upgrade': {
    description: 'Upgrades one weapon copy to the next rank for gold.',
    args: COPY_ARGS,
    result: obj({ id: str, price: int, range: int }),
    reasons: ['unknown_item', 'not_owned', 'not_upgradable', 'max_rank', 'crafting_too_low', 'not_enough_gold'],
  },
  'arsenal.stimToFight': {
    description: 'Takes one stimulant into the next battle.',
    args: args({ id: ID }),
    result: obj({ id: str, toFight: int, left: int }),
    reasons: ['unknown_item', 'not_owned', 'limit_reached'],
  },
  'arsenal.equip': {
    description: 'Equips a flag, a grave or an implant.',
    args: args({ category: oneOfValues('flags', 'rip', 'impl'), id: ID }),
    result: obj({ category: str, id: str, current: int }),
    reasons: ['unknown_item', 'not_owned', 'not_available', 'already_equipped'],
  },
  'arsenal.readBook': {
    description: 'Reads a book: learns its recipes.',
    args: args({ id: ID }),
    result: obj({ id: str, recipes: arrayOf(int) }),
    reasons: ['unknown_item', 'not_owned', 'not_usable', 'no_new_recipes'],
  },
  'arsenal.clearQuickSlot': {
    description: 'Clears a quick weapon slot.',
    args: args({ key: oneOfValues('z', 'x', 'c', 'v', 'b', 'n', 'm', 'k', 'l') }),
    result: obj({ key: str }),
    reasons: [],
  },
  'craft.assemble': {
    description: 'Assembles the items of the crafting stack (up to 6 slots, null = empty) by a recipe.',
    args: args({ stack: { type: 'array', maxItems: 6, items: { anyOf: [CRAFT_ITEM, { type: 'null' }] } } }),
    result: CRAFT_RESULT,
    reasons: ['unknown_item', 'not_owned', 'not_available', 'crafting_too_low', 'limit_reached'],
  },
  'craft.disassemble': {
    description: 'Disassembles one crafted weapon copy.',
    args: args({ item: CRAFT_ITEM }),
    result: CRAFT_RESULT,
    reasons: ['unknown_item', 'not_owned', 'not_available'],
  },
  'bank.exchange': {
    description: 'Exchanges diamonds for gold.',
    args: args({ diamonds: { type: 'integer', minimum: 1 } }),
    result: obj({ diamonds: int, gold: int }),
    reasons: ['not_enough_diam'],
  },
  'roulette.bet': {
    description: 'One roulette bet (gold 500 / 3000 / 10000 or diamonds 5 / 25 / 50) on the server random source.',
    args: args({ currency: CURRENCY, amount: { type: 'integer', minimum: 1, description: 'gold: 500, 3000, 10000; diam: 5, 25, 50' } }),
    result: obj({ currency: CURRENCY, amount: int, prizeClass: described(int, '1..7, 0 = nothing'), weapon: described(str, "'' = nothing"), added: int }),
    reasons: ['not_enough_gold', 'not_enough_diam', 'limit_reached'],
  },
  'steal.attempt': {
    description: 'Tries to steal an item from the shop (a chance; a failure offers the apologies).',
    args: args({ id: ID }),
    result: obj({ id: str, success: bool, prob: num, apology: APOLOGY }),
    reasons: ['shop_closed', 'unknown_item', 'not_available', 'limit_reached'],
  },
  'steal.apology': {
    description: 'Apologizes after a failed steal (luck or merchant).',
    args: args({ kind: oneOfValues('luck', 'merch') }),
    result: obj({ kind: str, success: bool, prob: num, apology: APOLOGY }),
    reasons: ['no_pending_steal', 'already_tried', 'not_available', 'not_enough_gold', 'not_enough_diam'],
  },
  'auction.buy': {
    description: 'Buys the Annihilator of the auction at the price the player saw (diamonds from the game state).',
    args: args({ expectedPrice: { type: 'integer', minimum: 1, maximum: 1_000_000_000 } }),
    result: obj({ paid: int, auction: AUCTION_STATE, mine: AUCTION_HOLDINGS }),
    reasons: ['price_changed', 'already_holder', 'not_enough_diam'],
    service: true,
  },

  // --- battles (solo)
  'battle.start': {
    description: 'Starts a solo battle: a stage of career / missions / survival / boss, or an assault zone (its stage and owner come from the server). The server keeps the battle pending until battle.finish.',
    args: args(
      {
        mode: oneOfValues('career', 'missions', 'survival', 'boss', 'assault'),
        stage: { type: 'integer', minimum: 1, maximum: 1000, description: 'career / missions / survival / boss' },
        zone: { type: 'integer', minimum: 1, maximum: 17, description: 'assault' },
        teamName: { type: 'string', maxLength: 64 },
      },
      ['stage', 'zone', 'teamName'],
    ),
    result: obj(
      {
        battleId: str,
        mode: str,
        stage: int,
        zoneStage: int,
        seed: described(int, 'battle seed 0..399'),
        stimsInBattle: mapOf(int),
        annihilators: int,
        teamName: str,
        papaHere: bool,
        tired: TIRED,
        endedBattle: obj({ battleId: str, reason: oneOfValues('replaced', 'abandoned') }),
      },
      ['zoneStage', 'endedBattle'],
    ),
    reasons: ['stage_locked', 'stage_not_ready', 'already_won', 'too_tired', 'level_too_low', 'zone_owned', 'zone_clan_owned', 'not_available', 'limit_reached'],
    service: true,
  },
  'battle.finish': {
    description:
      'Finishes the pending battle with its whole input (command log in global ticks) and the claimed outcome. The server re-simulates it when it has a battle simulator (verdict verified / rejected; the simulated outcome is applied) or applies the claim (unverified).',
    args: args({
      battleId: { type: 'string', pattern: '^[A-Za-z0-9_-]{1,64}$' },
      claim: args(
        {
          winner: described(int, '1 = the player won; 0 = nobody'),
          mode: oneOfValues('career', 'missions', 'survival', 'boss', 'assault', 'just'),
          stage: int,
          zone: int,
          ach_killed: KILLS,
          ach_paralyzed: int,
          ach_annihilated: int,
          ach_injure: bool,
          ach_kill: bool,
          graves: arrayOf(args({ name: str, cpu_level: int })),
        },
        ['stage', 'zone', 'ach_killed', 'ach_paralyzed', 'ach_annihilated', 'ach_injure', 'ach_kill', 'graves'],
      ),
      log: COMMAND_LOG_SCHEMA,
    }),
    result: obj(
      {
        battleId: str,
        verdict: oneOfValues('verified', 'rejected', 'unverified'),
        winner: int,
        win: bool,
        refused: oneOfValues('battle_state_changed', 'battle_mismatch', 'abandoned'),
        endGame: nullable(openObject("the game's end-game object (win / new level / surprise screens)")),
        granted: obj({ gold: int, exp: int, rating: int, levels: int, diam: int, skillPts: int, perkPts: int, weapons: arrayOf(str) }),
        graves: arrayOf(BATTLE_GRAVE),
        captured: bool,
        captureBlocked: oneOfValues('own', 'clan', 'stage_changed', 'not_verified'),
        level: int,
        replayId: str,
      },
      ['refused', 'captured', 'captureBlocked'],
    ),
    reasons: ['no_pending_battle', 'simulation_unavailable'],
    service: true,
  },
  'battle.forfeit': {
    description: 'Gives the pending battle up (a loss; the start costs stay).',
    args: args({ battleId: { type: 'string', pattern: '^[A-Za-z0-9_-]{1,64}$' } }),
    result: obj({ battleId: str, forfeited: oneOfValues(true) }),
    reasons: ['no_pending_battle'],
    service: true,
  },
  'grave.search': {
    description: 'Searches one grave (1-based index) of the last won battle.',
    args: args({ index: { type: 'integer', minimum: 1, maximum: 16 } }),
    result: obj({ index: int, ptype: described(str, "'' | 'stim' | 'gold' | 'diam' | 'obj' | 'weapon'"), id: described(str, 'item id, or the amount of gold / diamonds') }),
    reasons: ['not_available', 'already_searched'],
    service: true,
  },

  // --- store (in-app purchases; feature 'store')
  'store.redeem': {
    description:
      "Credits a store purchase to the player once: the server asks the store (Google Play Developer API) whether the purchase token is a completed purchase of that product, adds the catalogue's gold / diamonds (store.catalog) and consumes the purchase. The same purchase again answers status 'already_credited' (nothing added). Consume the purchase in the billing library when `consume` is true. A pending payment is refused with 'purchase_pending' (redeem again once it completes; the server also credits it on its own from the store's notification). UNAVAILABLE 'store_unavailable': retry with the same opId.",
    args: args(
      {
        provider: oneOfValues('google-play', 'app-store', 'dev-test'),
        productId: { type: 'string', minLength: 1, maxLength: 139 },
        purchaseToken: { type: 'string', minLength: 1, maxLength: 4096, description: 'the purchase token of the store (Play: Purchase.getPurchaseToken())' },
        orderId: { type: 'string', maxLength: 128, description: 'the store order id, when the client knows it (informational)' },
      },
      ['orderId'],
    ),
    result: obj({
      status: oneOfValues('credited', 'already_credited'),
      purchaseId: str,
      productId: str,
      gold: int,
      diam: int,
      items: mapOf(int, 'stimulator id -> count'),
      multiplier: described(int, '2 = the first purchase x2 was applied'),
      test: described(bool, 'a store test purchase (license tester)'),
      consume: described(bool, 'consume the purchase in the billing library now'),
    }),
    reasons: ['provider_disabled', 'product_unknown', 'purchase_invalid', 'purchase_pending', 'purchase_voided', 'purchase_other_account', 'store_unavailable'],
    service: true,
  },
};

/** The ops of the catalogue. */
export const COMMAND_OPS: readonly string[] = Object.keys(COMMAND_CATALOGUE).sort();
